import React, { useEffect, useRef, useState } from 'react';
import { Badge, Button, ChevronDownIcon, DateInput, LinkChainIcon, ListTreeIcon, NumberInput, PlusIcon, Select, TextInput, TrashIcon } from '../../../components/ui';
import { useTheme } from '../../../contexts/ThemeContext';
import { cn } from '../../../lib/utils';
import { acctTableHeader } from './typography';
import { applyDocumentLinePatch } from './balanceUtils';
import type { DocumentLineForm } from './documentLineTypes';
import { newDocumentLine } from './documentLineTypes';
import { dueDateFromPaymentTerms } from './paymentTerms';
import { buildLineFromLinkSelection, resolveRateForCurrency } from './linkPickerLineUtils';
import { CoaAccountPickerModal } from './CoaAccountPickerModal';
import { OpenItemLinkPickerModal } from './OpenItemLinkPickerModal';
import type { DocumentFormTemplateId } from '../documents/formTemplates';
type Option = { value: string; label: string };

type Props = {
  lines: DocumentLineForm[];
  companies: Option[];
  accounts: Option[];
  currencies: Option[];
  accountMeta: Record<string, {
    allowOpenItems?: boolean;
    paymentTermsDays?: number | null;
    currencyIds?: number[];
    companyIds?: number[];
  }>;
  postingDate?: string;
  onChange: React.Dispatch<React.SetStateAction<DocumentLineForm[]>>;
  readOnly?: boolean;
  onAddLine?: () => void;
  /** Switches line-entry UX (hints, extended row defaults, link picker emphasis). */
  formTemplateId?: DocumentFormTemplateId;
  defaultShowExtended?: boolean;
  emphasizeLinkPicker?: boolean;
};

const HEADERS = ['#', 'Company', 'Account', 'Narrative', 'CCY', 'Rate', 'Debit', 'Credit', 'Dr USD', 'Cr USD', ''] as const;

const SUB_ROW_COLSPAN = HEADERS.length - 2;

const CELL = 'px-1 py-0.5 align-top max-w-0 overflow-hidden';
const COMPACT_SELECT = '[&>div:nth-child(2)]:min-h-8 [&>div:nth-child(2)]:h-8 [&>div:nth-child(2)]:py-0.5 [&>div:nth-child(2)]:px-1.5 [&>div:nth-child(2)]:text-xs';
const W_COMPANY = '8.75rem';
const W_LINK_BADGE = '5rem';
const DETAIL_GRID = 'grid-cols-[5rem_8.75rem_8.75rem_8.75rem_8.75rem_minmax(0,1fr)]';
const ICON_BTN = 'shrink-0 h-8 w-8';

const hasExtendedLineData = (line: DocumentLineForm) =>
  !!(line.linkNumber?.trim() || line.plPeriodFrom || line.plPeriodTo || line.dn?.trim() || line.dueDate || line.notes?.trim());

export const DocumentLinesGrid: React.FC<Props> = ({
  lines,
  companies,
  accounts,
  currencies,
  accountMeta,
  postingDate = '',
  onChange,
  readOnly = false,
  onAddLine,
  formTemplateId = 'general',
  defaultShowExtended = false,
  emphasizeLinkPicker = false,
}) => {
  const { colors, theme } = useTheme();
  const isDark = theme === 'dark';
  const userToggledRef = useRef(false);
  const rateCacheRef = useRef(new Map<string, { rate: number | null; rateDate: string | null }>());
  const [showExtended, setShowExtended] = useState(() => defaultShowExtended || lines.some(hasExtendedLineData));
  const [coaPickerLineKey, setCoaPickerLineKey] = useState<string | null>(null);
  const [linkPickerLineKey, setLinkPickerLineKey] = useState<string | null>(null);

  useEffect(() => {
    if (!userToggledRef.current && lines.some(hasExtendedLineData)) {
      setShowExtended(true);
    }
  }, [lines]);

  useEffect(() => {
    if (userToggledRef.current) return;
    setShowExtended(defaultShowExtended || lines.some(hasExtendedLineData));
  }, [formTemplateId, defaultShowExtended, lines]);

  useEffect(() => {
    rateCacheRef.current.clear();
  }, [postingDate]);

  const extendedDataCount = lines.filter(hasExtendedLineData).length;

  const updateLine = (key: string, patch: Partial<DocumentLineForm>) => {
    onChange((current) => current.map((l) => {
      if (l.key !== key) return l;
      let next = applyDocumentLinePatch(l, patch);
      if (patch.accountId !== undefined) {
        const meta = accountMeta[patch.accountId];
        const allowedCurrencies = meta?.currencyIds ?? [];
        if (allowedCurrencies.length > 0 && next.currencyId && !allowedCurrencies.includes(Number(next.currencyId))) {
          next = { ...next, currencyId: '', rate: '' };
        }
        const allowedCompanies = meta?.companyIds ?? [];
        if (allowedCompanies.length > 0 && next.companyId && !allowedCompanies.includes(Number(next.companyId))) {
          next = { ...next, companyId: '' };
        }
        if (meta?.allowOpenItems && meta.paymentTermsDays != null && postingDate) {
          const termsDue = dueDateFromPaymentTerms(postingDate, meta.paymentTermsDays);
          if (termsDue) {
            next = { ...next, dueDate: termsDue };
          }
        }
      }
      return next;
    }));
  };

  const handleCurrencyChange = async (lineKey: string, currencyId: string) => {
    if (!currencyId) {
      updateLine(lineKey, { currencyId: '', rate: '' });
      return;
    }

    updateLine(lineKey, { currencyId });

    const resolved = await resolveRateForCurrency(currencyId, postingDate, rateCacheRef.current);
    if (!resolved) return;

    onChange((current) =>
      current.map((l) => {
        if (l.key !== lineKey || l.currencyId !== currencyId) return l;
        return applyDocumentLinePatch(l, { rate: resolved });
      }),
    );
  };

  const currenciesForLine = (accountId: string) => {
    const allowed = accountMeta[accountId]?.currencyIds ?? [];
    if (!allowed.length) return currencies;
    return currencies.filter((c) => allowed.includes(Number(c.value)));
  };

  const companiesForLine = (accountId: string) => {
    const allowed = accountMeta[accountId]?.companyIds ?? [];
    if (!allowed.length) return companies;
    return companies.filter((c) => allowed.includes(Number(c.value)));
  };

  const removeLine = (key: string) => onChange((current) => current.filter((l) => l.key !== key));

  const addLine = () => {
    if (onAddLine) onAddLine();
    else onChange((current) => [...current, newDocumentLine()]);
  };

  const toggleExtended = () => {
    userToggledRef.current = true;
    setShowExtended((open) => !open);
  };

  const rowSpan = showExtended ? 2 : 1;
  const coaPickerLine = coaPickerLineKey ? lines.find((line) => line.key === coaPickerLineKey) ?? null : null;
  const linkPickerLine = linkPickerLineKey ? lines.find((line) => line.key === linkPickerLineKey) ?? null : null;

  return (
    <div className={cn('rounded-xl border overflow-hidden', colors.border, colors.surface)}>
      <div className={cn('flex items-center justify-between gap-2 border-b px-3 py-1.5', colors.border, colors.headerBg)}>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={toggleExtended}
          className="h-8 gap-1.5 px-2 text-xs font-medium"
          aria-expanded={showExtended}
        >
          <ChevronDownIcon className={cn('h-4 w-4 transition-transform', showExtended && 'rotate-180')} />
          Line details
          {!showExtended && extendedDataCount > 0 && (
            <span
              className={cn(
                'rounded-full px-1.5 py-0.5 text-xs font-semibold tabular-nums',
                colors.surfaceHover,
                colors.text,
              )}
            >
              {extendedDataCount}
            </span>
          )}
        </Button>
        <span className={cn('text-xs tabular-nums', colors.textMuted)}>{lines.length} line{lines.length === 1 ? '' : 's'}</span>
      </div>
      <div className="overflow-x-auto" data-enter-nav-root>
        <table className="w-full min-w-[68rem] table-fixed text-xs">
          <colgroup>
            <col className="w-9" />
            <col style={{ width: W_COMPANY }} />
            <col className="w-[11rem]" />
            <col className="w-[11rem]" />
            <col className="w-[4.25rem]" />
            <col className="w-[4.75rem]" />
            <col className="w-[5.5rem]" />
            <col className="w-[5.5rem]" />
            <col className="w-[5.5rem]" />
            <col className="w-[5.5rem]" />
            <col className="w-9" />
          </colgroup>
          <thead className={cn('sticky top-0 z-[1] border-b backdrop-blur-sm', colors.border, colors.headerBg)}>
            <tr>
              {HEADERS.map((h) => (
                <th
                  key={h || 'actions'}
                  className={cn(
                    acctTableHeader,
                    'px-1 py-1',
                    colors.textSecondary,
                    h === '#' && 'text-center',
                    (h === 'Debit' || h === 'Credit' || h === 'Dr USD' || h === 'Cr USD' || h === 'Rate') && 'text-right',
                  )}
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          {lines.map((line, index) => {
            const meta = line.accountId ? accountMeta[line.accountId] : undefined;
            const showLink = meta?.allowOpenItems;
            const dueLabel =
              meta?.paymentTermsDays != null && showLink
                ? `Due (Net ${meta.paymentTermsDays})`
                : 'Due';
            const isEven = index % 2 === 0;
            return (
              <tbody
                key={line.key}
                className={cn(
                  'border-b border-l-2 last:border-b-0',
                  colors.border,
                  isEven ? 'border-l-sky-500/70' : 'border-l-indigo-500/70',
                  isEven ? colors.surface : colors.headerBg,
                  colors.interactiveHover,
                )}
              >
                  <tr>
                    <td
                      rowSpan={rowSpan}
                      className={cn(
                        CELL,
                        'border-r text-center',
                        colors.border,
                        colors.surfaceHover,
                      )}
                    >
                      <span
                        className={cn(
                          'inline-flex h-5 min-w-[1.25rem] items-center justify-center rounded-full px-1 text-[11px] font-semibold tabular-nums',
                          colors.surfaceHover,
                          colors.text,
                        )}
                      >
                        {index + 1}
                      </span>
                    </td>
                    <td className={CELL}>
                      <Select
                        enterNav
                        disabled={readOnly}
                        className={COMPACT_SELECT}
                        value={line.companyId}
                        onChange={(e) => updateLine(line.key, { companyId: e.target.value })}
                        options={[{ value: '', label: '—' }, ...companiesForLine(line.accountId)]}
                      />
                    </td>
                    <td className={CELL}>
                      <div className="flex max-w-full min-w-0 items-stretch gap-0.5">
                        {!readOnly && (
                          <Button
                            type="button"
                            variant="outline"
                            size="icon"
                            className={cn(
                              ICON_BTN,
                              colors.primaryFg,
                              colors.primaryBorderAll,
                              colors.interactiveHover,
                            )}
                            title="Browse chart of accounts"
                            aria-label="Browse chart of accounts"
                            onClick={() => setCoaPickerLineKey(line.key)}
                          >
                            <ListTreeIcon className="h-3.5 w-3.5" />
                          </Button>
                        )}
                        <div className="max-w-full min-w-0 flex-1 overflow-hidden">
                          <Select
                            enterNav
                            disabled={readOnly}
                            className={COMPACT_SELECT}
                            value={line.accountId}
                            onChange={(e) => updateLine(line.key, { accountId: e.target.value })}
                            options={[{ value: '', label: '—' }, ...accounts]}
                          />
                        </div>
                        {!readOnly && showLink && (
                          <Button
                            type="button"
                            variant="outline"
                            size="icon"
                            className={cn(
                              ICON_BTN,
                              colors.primaryFg,
                              colors.primaryBorderAll,
                              colors.interactiveHover,
                              emphasizeLinkPicker && 'ring-2 ring-indigo-500/50',
                            )}
                            title={
                              line.companyId
                                ? emphasizeLinkPicker
                                  ? 'Pick open items to pay — imports currency, rate, and credit amount'
                                  : 'Open links — outstanding and without link number'
                                : 'Select company first'
                            }
                            aria-label="Open links"
                            disabled={!line.companyId || !line.accountId}
                            onClick={() => setLinkPickerLineKey(line.key)}
                          >
                            <LinkChainIcon className="h-3.5 w-3.5" />
                          </Button>
                        )}
                      </div>
                    </td>
                    <td className={CELL}>
                      <TextInput
                        enterNav
                        size="sm"
                        disabled={readOnly}
                        className="min-h-8 h-8"
                        value={line.narrative}
                        onChange={(e) => updateLine(line.key, { narrative: e.target.value })}
                        placeholder="Narrative"
                      />
                    </td>
                    <td className={CELL}>
                      <Select
                        enterNav
                        disabled={readOnly}
                        className={COMPACT_SELECT}
                        value={line.currencyId}
                        onChange={(e) => { void handleCurrencyChange(line.key, e.target.value); }}
                        options={[{ value: '', label: '—' }, ...currenciesForLine(line.accountId)]}
                      />
                    </td>
                    <td className={CELL}>
                      <NumberInput
                        enterNav
                        size="sm"
                        disabled={readOnly}
                        placeholder=""
                        className="min-h-8 h-8 text-right tabular-nums"
                        value={line.rate}
                        onChange={(e) => updateLine(line.key, { rate: String(e.target.value ?? '') })}
                      />
                    </td>
                    <td className={CELL}>
                      <NumberInput
                        enterNav
                        size="sm"
                        disabled={readOnly}
                        placeholder=""
                        className="min-h-8 h-8 text-right tabular-nums"
                        value={line.debitLocal}
                        onChange={(e) => updateLine(line.key, { debitLocal: String(e.target.value ?? '') })}
                      />
                    </td>
                    <td className={CELL}>
                      <NumberInput
                        enterNav
                        size="sm"
                        disabled={readOnly}
                        placeholder=""
                        className="min-h-8 h-8 text-right tabular-nums"
                        value={line.creditLocal}
                        onChange={(e) => updateLine(line.key, { creditLocal: String(e.target.value ?? '') })}
                      />
                    </td>
                    <td className={CELL}>
                      <NumberInput
                        enterNav
                        size="sm"
                        disabled={readOnly}
                        placeholder=""
                        className="min-h-8 h-8 text-right tabular-nums"
                        value={line.debitUSD}
                        onChange={(e) => updateLine(line.key, { debitUSD: String(e.target.value ?? '') })}
                      />
                    </td>
                    <td className={CELL}>
                      <NumberInput
                        enterNav
                        size="sm"
                        disabled={readOnly}
                        placeholder=""
                        className="min-h-8 h-8 text-right tabular-nums"
                        value={line.creditUSD}
                        onChange={(e) => updateLine(line.key, { creditUSD: String(e.target.value ?? '') })}
                      />
                    </td>
                    <td rowSpan={rowSpan} className={cn(CELL, 'w-9 align-top')}>
                      {!readOnly && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className={cn(
                            ICON_BTN,
                            isDark ? 'text-red-400' : 'text-red-600',
                            colors.interactiveHover,
                          )}
                          onClick={() => removeLine(line.key)}
                          title="Remove line"
                          aria-label="Remove line"
                        >
                          <TrashIcon className="w-3.5 h-3.5" />
                        </Button>
                      )}
                    </td>
                  </tr>
                  {showExtended && (
                    <tr>
                      <td colSpan={SUB_ROW_COLSPAN} className={cn('px-2 py-2 border-t border-dashed', colors.border)}>
                        <div className={cn('grid w-full items-center gap-2 min-w-0', DETAIL_GRID)}>
                          <div
                            className="flex min-h-8 h-8 w-full items-center min-w-0"
                            style={{ maxWidth: W_LINK_BADGE }}
                            title="Link #"
                          >
                            {showLink && line.linkNumber?.trim() ? (
                              <Badge variant="info" className="font-normal tabular-nums max-w-full truncate">
                                {line.linkNumber.trim()}
                              </Badge>
                            ) : (
                              <Badge variant="secondary" className="font-normal w-full justify-center text-center">
                                {showLink ? 'No link' : '—'}
                              </Badge>
                            )}
                          </div>
                          <DateInput
                            enterNav
                            disabled={readOnly}
                            className="min-h-8 h-8 py-0.5"
                            value={line.plPeriodFrom}
                            onChange={(e) => updateLine(line.key, { plPeriodFrom: e.target.value })}
                            showFormatHint={false}
                            placeholder="P&L from"
                          />
                          <DateInput
                            enterNav
                            disabled={readOnly}
                            className="min-h-8 h-8 py-0.5"
                            value={line.plPeriodTo}
                            onChange={(e) => updateLine(line.key, { plPeriodTo: e.target.value })}
                            showFormatHint={false}
                            placeholder="P&L to"
                          />
                          <TextInput
                            enterNav
                            size="sm"
                            disabled={readOnly}
                            className="min-h-8 h-8"
                            value={line.dn}
                            onChange={(e) => updateLine(line.key, { dn: e.target.value })}
                            placeholder="DN"
                          />
                          <DateInput
                            enterNav
                            disabled={readOnly}
                            className="min-h-8 h-8 py-0.5"
                            value={line.dueDate}
                            onChange={(e) => updateLine(line.key, { dueDate: e.target.value })}
                            showFormatHint={false}
                            placeholder={dueLabel}
                          />
                          <TextInput
                            enterNav
                            size="sm"
                            disabled={readOnly}
                            className="min-h-8 h-8"
                            value={line.notes}
                            onChange={(e) => updateLine(line.key, { notes: e.target.value })}
                            placeholder="Notes"
                          />
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              );
            })}
        </table>
      </div>
      {!readOnly && (
        <div className={cn('px-3 py-2 border-t flex items-center justify-end gap-2', colors.border, colors.surfaceHover)}>
          <Button type="button" variant="outline" size="sm" onClick={addLine} className="gap-1.5">
            <PlusIcon className="h-4 w-4" />
            Add line
          </Button>
        </div>
      )}

      <CoaAccountPickerModal
        isOpen={coaPickerLineKey != null}
        onClose={() => setCoaPickerLineKey(null)}
        value={coaPickerLine?.accountId}
        postingOnly
        onSelect={(accountId) => {
          if (!coaPickerLineKey) return;
          updateLine(coaPickerLineKey, { accountId: String(accountId) });
          setCoaPickerLineKey(null);
        }}
      />

      <OpenItemLinkPickerModal
        isOpen={linkPickerLineKey != null}
        onClose={() => setLinkPickerLineKey(null)}
        companyId={linkPickerLine?.companyId ? Number(linkPickerLine.companyId) : null}
        accountId={linkPickerLine?.accountId ? Number(linkPickerLine.accountId) : null}
        asOfDate={postingDate}
        value={linkPickerLine?.linkNumber}
        onSelect={async (selections) => {
          if (!linkPickerLineKey || selections.length === 0) return;
          const baseLine = lines.find((line) => line.key === linkPickerLineKey);
          if (!baseLine) return;

          const ctx = {
            postingDate,
            currencies,
            rateCache: rateCacheRef.current,
          };

          const firstLine = await buildLineFromLinkSelection(baseLine, selections[0], ctx);
          const extraLines = await Promise.all(
            selections.slice(1).map((selection) =>
              buildLineFromLinkSelection(
                {
                  ...newDocumentLine(),
                  companyId: baseLine.companyId,
                  accountId: baseLine.accountId,
                },
                selection,
                ctx,
              ),
            ),
          );

          onChange((current) => {
            const baseIdx = current.findIndex((line) => line.key === linkPickerLineKey);
            if (baseIdx < 0) return current;
            const next = [...current];
            next[baseIdx] = firstLine;
            next.splice(baseIdx + 1, 0, ...extraLines);
            return next;
          });
          userToggledRef.current = true;
          setShowExtended(true);
          setLinkPickerLineKey(null);
        }}
      />
    </div>
  );
};
